/**
 * @file useFontSize.js
 * @description A composable for managing font size settings throughout the application.
 * This handles font size selection, application to the DOM, and persistence in user settings.
 */

import { computed, watch } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

/**
 * Font size options with their multiplier values
 * Base is 1.0 (16px equivalent), other sizes scale proportionally
 * @type {Object}
 */
const FONT_SIZE_OPTIONS = {
  SMALL: '0.875', // 14px equivalent
  DEFAULT: '1', // 16px equivalent
  LARGE: '1.125', // 18px equivalent
  EXTRA_LARGE: '1.25', // 20px equivalent
};

/**
 * Migration map: Convert old pixel values to new multiplier values
 * @type {Object}
 */
const LEGACY_PIXEL_TO_MULTIPLIER = {
  '14px': '0.875',
  '16px': '1',
  '18px': '1.125',
  '20px': '1.25',
};

/**
 * Convert legacy pixel value to multiplier if needed
 * @param {string} value - Font size value (could be '18px' or '1.125')
 * @returns {string} Multiplier value
 */
const normalizeToMultiplier = value => {
  // If the user hasn't chosen a font size yet, default to SMALL for Interface settings.
  if (!value) return FONT_SIZE_OPTIONS.SMALL;

  // If it's a legacy pixel value, convert it
  if (LEGACY_PIXEL_TO_MULTIPLIER[value]) {
    return LEGACY_PIXEL_TO_MULTIPLIER[value];
  }

  // If it ends with 'px', it's an unknown legacy value - use default
  if (String(value).endsWith('px')) {
    return FONT_SIZE_OPTIONS.DEFAULT;
  }

  // Already a multiplier value
  return value;
};

/**
 * Array of font size option keys
 * @type {Array<string>}
 */
const FONT_SIZE_NAMES = Object.keys(FONT_SIZE_OPTIONS);

/**
 * Get font size label translation key
 *
 * @param {string} name - Font size name
 * @returns {string} Translation key
 */
const getFontSizeLabelKey = name =>
  `PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.OPTIONS.${name}`;

/**
 * Create font size option object
 *
 * @param {Function} t - Translation function
 * @param {string} name - Font size name
 * @returns {Object} Font size option with value and label
 */
const createFontSizeOption = (t, name) => ({
  value: FONT_SIZE_OPTIONS[name],
  label: t(getFontSizeLabelKey(name)),
});

/**
 * Apply font size multiplier to document root
 * This sets a CSS custom property that can be used throughout the app
 *
 * @param {string} value - Font size value (could be legacy '18px' or new '1.125')
 */
const applyFontSizeToDOM = value => {
  // Normalize to multiplier (handles legacy pixel values)
  const mult = normalizeToMultiplier(value);

  // Set CSS custom property for multiplier (used by calc() in CSS)
  document.documentElement.style.setProperty('--wlab-font-multiplier', mult);

  // Also set root font-size in pixels for rem-based elements
  // multiplier 1 = 16px, 1.125 = 18px, etc.
  const basePx = 16 * parseFloat(mult);
  document.documentElement.style.setProperty('font-size', `${basePx}px`);
};

/**
 * Font size management composable
 *
 * @returns {Object} Font size utilities and state
 * @property {Array} fontSizeOptions - Array of font size options for select components
 * @property {import('vue').ComputedRef<string>} currentFontSize - Current font size from UI settings
 * @property {Function} applyFontSize - Function to apply font size to document
 * @property {Function} updateFontSize - Function to update font size in settings with alert feedback
 */
export const useFontSize = () => {
  const { uiSettings, updateUISettings } = useUISettings();
  const { t } = useI18n();

  /**
   * Font size options for select dropdown
   * @type {Array<{value: string, label: string}>}
   */
  const fontSizeOptions = computed(() =>
    FONT_SIZE_NAMES.map(name => createFontSizeOption(t, name))
  );

  /**
   * Current font size from UI settings (normalized to multiplier)
   * @type {import('vue').ComputedRef<string>}
   */
  const currentFontSize = computed(() =>
    normalizeToMultiplier(uiSettings.value.font_size)
  );

  /**
   * Apply font size to document root
   * @param {string} pixelValue - Font size in pixels (e.g., '16px')
   * @returns {void}
   */
  const applyFontSize = pixelValue => {
    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => applyFontSizeToDOM(pixelValue));
  };

  /**
   * Update font size in settings and apply to document
   * Shows success/error alerts
   * @param {string} pixelValue - Font size in pixels (e.g., '16px')
   * @returns {Promise<void>}
   */
  const updateFontSize = async pixelValue => {
    try {
      await updateUISettings({ font_size: pixelValue });
      applyFontSize(pixelValue);
      useAlert(
        t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.UPDATE_SUCCESS')
      );
    } catch (error) {
      useAlert(
        t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.UPDATE_ERROR')
      );
    }
  };

  // Watch for changes to the font size in UI settings
  watch(
    () => uiSettings.value.font_size,
    newSize => {
      applyFontSize(newSize);
    },
    { immediate: true }
  );

  return {
    fontSizeOptions,
    currentFontSize,
    applyFontSize,
    updateFontSize,
  };
};

export default useFontSize;
